/**
 * Transaction Ledger screen (Operations — DNKADM-0149 U2; money-only DNKADM-0154 U1;
 * Payments queue fold-in + Upcoming memberships DNKADM-0377).
 *
 * **Money only.** One screen with a **Payments / Upcoming (card-on-file) /
 * Upcoming memberships** toggle: the payment-transactions ledger (charges, dues,
 * refunds) with a revenue rollup strip and a **Needs action** pending-charge queue
 * (`UpcomingChargesQueue`, relocated here from the sidebar in DNKADM-0154 and
 * folded onto Payments in DNKADM-0377), the read-only **Upcoming (card-on-file)**
 * no-show-charge forecast (DNKADM-0215), and the read-only **Upcoming memberships**
 * next-charge forecast (`GET /v2/admin/memberships/upcoming-charges`, DNKAPI-0533).
 * Distinct from the Activity log (action history) — this is what *moved* or could
 * move. Operations surface (every operator), NOT behind `RequireAdmin`.
 * Under-title copy is tab-specific; an empty Needs action queue is a quiet
 * one-line notice (DNKADM-0378).
 *
 * **A STAFF session (DNKADM-0375)** gets the ledger too: dink-api opens
 * `/v2/admin/payments*`, the pending-charge queue,
 * `/v2/admin/reservations/card-on-file` and (DNKAPI-0675, DNKADM-0465)
 * `/v2/admin/memberships/upcoming-charges` to STAFF, scoped to the caller's locations, so
 * STAFF has all three tabs. Two things stay OWNER/ADMIN and are absent for STAFF: order
 * Refund/Void and the queue's Adjust amount / Waive. Nothing changes for any other
 * operator.
 *
 * The **tenant-wide Credit Ledger is retired** (DNKADM-0154): credits are inherently
 * per-member and holistic, so they are seen only on the Member → Credits tab. The unused
 * `CreditsLedger` export was removed (DNKADM-0457).
 *
 * **View state lives in the URL (DNKADM-0430 P1-B).** The tab and the member filter
 * (`?tab=payments|upcoming-card-on-file|upcoming-memberships&contactId=<id>`, so
 * DNKADM-0150's member deep-links land pre-filtered) are read from AND written back to
 * the query string, as are every panel's filters and page, so a copied link or a reload
 * reopens the same view. Each panel owns distinct param names so the tabs never clobber
 * each other: Payments `from`/`to`/`kind`/`status`/`page`, the Needs action queue
 * `naStatus`/`na24h`/`naPage`, the card-on-file forecast `cof24h`/`cofPage`, the
 * memberships forecast `memPage`. A tab change writes only `tab`, so each panel's view is
 * still there when the operator comes back to it. Writes replace the history entry
 * (`useListUrlState`'s default), so Back leaves the screen. Every list here is server
 * paged, so none has a sort control in Phase 1. `?tab=upcoming-payments` is an
 * alias for Payments (the retired third tab). The member filter (Payments only) is a
 * shared chip driven by the reusable `MemberSearchDialog` (U1); the Payments ledger
 * renders through the reusable `PaymentTransactionsTable` so DNKADM-0150 can reuse it
 * member-scoped.
 *
 * The Payments ledger's filters (date-range default last-30-days, clearable to empty /
 * kind / status) plus the shared member chip sit in the shared `ListToolbar`; 1-indexed UI paging (`listPaymentTransactions`
 * converts to the 0-indexed wire, DNKADM-0148), and the loading-skeleton / empty /
 * error+retry states mirroring `ConfigAuditLog`/`UpcomingChargesQueue`. Enrichment
 * (`contactName`) is best-effort — a null name falls back to the raw id, never an error.
 * The revenue strip is best-effort: a failure leaves the ledger working. In Front Desk
 * view the strip is hidden AND its fetch is skipped (DNKADM-0307) — a shoulder-surfing
 * mitigation for the counter iPad, not access control; the ledger itself stays. The
 * Needs action queue keeps its own filters/paging/actions unchanged.
 *
 * The Payments ledger opts INTO the table's order **Refund / Void** action
 * (DNKADM-0251) by passing `onReversed`: a confirmed reversal re-reads the ledger page
 * AND the revenue strip (two separate resources), so the reversed row, the new refund
 * row, and the window's totals all come from the server rather than a local patch.
 *
 * **Location scope (DNKADM-0153 / DNKADM-0215 U2 / DNKADM-0377).** There is NO
 * in-screen location control. The Payments ledger, Needs action queue, Upcoming
 * (card-on-file) forecast, and Upcoming memberships forecast are auto-scoped to the
 * app-bar service-location chooser (`useServiceLocation()`): a selected `id` is sent
 * as the location param and a location switch refetches. The current chooser has no
 * operator-selectable all-locations state, so the forecasts scope to the selected
 * service location when available.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { IconAction } from '@/components/IconAction';
import { MemberNameLink } from '@/components/MemberNameLink';
import { ListSelectFilter, ListToolbar } from '@/components/list/ListToolbar';
import {
  parseOption,
  parsePage,
  toPageParam,
  useListUrlState,
  type ListUrlState,
} from '@/components/list/useListUrlState';
import { Icon, inkAlpha, resolveTokens } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useStaffTier } from '@/auth/authHooks';
import { useTenantCurrency } from '@/hooks/useTenantCurrency';
import { useFrontDeskView } from '@/auth/frontDeskHooks';
import { useServiceLocation } from '@/location/useServiceLocation';
import { useCalendarZone } from '@/location/useCalendarZone';
import { useLocationName } from '@/location/useLocationName';
import { formatApiDate, rangeBounds } from '@/components/list/listDate';
import { formatMoney } from '@/utils/v2Format';
import {
  listPaymentTransactions,
  getRevenueByLocation,
  PAYMENT_TRANSACTION_KINDS,
  PAYMENT_TRANSACTION_STATUSES,
} from '@/api/paymentsTransactions';
import { listCardOnFileReservations, type Reservation } from '@/api/reservations';
import {
  listUpcomingMembershipCharges,
  type UpcomingMembershipCharge,
  type UpcomingMembershipChargesSummary,
} from '@/api/upcomingMembershipCharges';
import { getMember } from '@/api/members';
import { nonEmpty } from '@/utils/personLabel';
import { MemberSearchDialog } from '@/features/members/MemberSearchDialog';
import { UpcomingChargesQueue } from '@/features/pending-charges/UpcomingChargesScreen';
import { PaymentTransactionsTable } from './PaymentTransactionsTable';
import { humanizeToken } from './transactionFormat';
import { ScreenError } from '@/components/ScreenError';
import { DATE_TIME, formatInstant } from '@/utils/instantFormat';
import { isoClockAsIs, monthDayLabel } from '@/utils/clockTime';

const LEDGER_TABS = ['payments', 'upcoming-card-on-file', 'upcoming-memberships'] as const;
type LedgerTab = (typeof LEDGER_TABS)[number];

const LEDGER_TAB_COPY: Record<LedgerTab, string> = {
  payments:
    'Money that already moved at this location — charges, dues, and refunds. If a card-on-file booking still needs charge, adjust, or waive, it appears in Needs action above the list (~30 minutes after check-in). This is money only. Credits live on each member’s Credits tab. The Activity log records what was done.',
  'upcoming-card-on-file':
    'Bookings at this location that have a card on file and have not started yet. The no-show amounts are potential charges, not money that has moved. Nothing here has been billed.',
  'upcoming-memberships':
    'Known next membership charges for this location. Dates are on or about; amounts are estimates — do not quote them as a promise. Members with no trustworthy date (including some Square billing days) do not appear; that is not an error and does not mean nobody will be billed. Charges that already ran are on Payments.',
};

function transactionLedgerTabA11yProps(tab: LedgerTab) {
  return {
    id: `transaction-ledger-tab-${tab}`,
    'aria-controls': `transaction-ledger-tabpanel-${tab}`,
  };
}

function transactionLedgerPanelA11yProps(tab: LedgerTab) {
  return {
    role: 'tabpanel' as const,
    id: `transaction-ledger-tabpanel-${tab}`,
    'aria-labelledby': `transaction-ledger-tab-${tab}`,
    tabIndex: 0,
  };
}

const DEFAULT_PAGE_SIZE = 20;
const SKELETON_ROWS = 6;
const CARD_ON_FILE_WITHIN_HOURS = 24;
const FALLBACK_CURRENCY = 'USD';

/**
 * The default date window: today-30 through today, both inclusive, on the LOCATION's calendar
 * (DNKADM-0439; DNKADM-0437 V-18 had moved it off UTC onto the device's day). `zone` null is
 * the device.
 */
function defaultRange(zone: string | null): { from: string; to: string } {
  return rangeBounds(zone, 30);
}

/** A `YYYY-MM-DD` date param; anything else (a hand-edited link) is not a date. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The screen-level URL params (DNKADM-0430 P1-B): the tab, the member filter, and the
 * Payments ledger's filters and page. `from`/`to` default to the last 30 days, computed
 * once per mount (see {@link ledgerUrlDefaults}); a cleared date is written as `from=`
 * and reads back as `''` (no bound).
 */
type LedgerUrlDefaults = {
  tab: string;
  contactId: string;
  from: string;
  to: string;
  kind: string;
  status: string;
  page: string;
};

function ledgerUrlDefaults(zone: string | null): LedgerUrlDefaults {
  const range = defaultRange(zone);
  return {
    tab: 'payments',
    contactId: '',
    from: range.from,
    to: range.to,
    kind: '',
    status: '',
    page: '1',
  };
}

/** The card-on-file forecast's URL params (DNKADM-0430 P1-B); `cofPage` is 1-indexed. */
const CARD_ON_FILE_URL_DEFAULTS = { cof24h: '', cofPage: '1' } as const;
/** The memberships forecast's URL params (DNKADM-0430 P1-B); `memPage` is 1-indexed. */
const MEMBERSHIPS_URL_DEFAULTS = { memPage: '1' } as const;
/** The URL value of an "on" switch. */
const SWITCH_ON = '1';

export function TransactionLedgerScreen() {
  const t = resolveTokens(useTheme());
  // DNKADM-0375: order Refund/Void is OWNER/ADMIN on the server, so a STAFF session's
  // Payments ledger is read-only.
  const isStaffTier = useStaffTier();
  // DNKADM-0430 P1-B: the tab, the member filter and the Payments filters live in the URL
  // (DNKADM-0150 deep links; `?tab=upcoming-payments` aliases to Payments — DNKADM-0377).
  // The defaults are computed once per calendar zone (DNKADM-0439): the hook's memo and `set`
  // depend on them, and the zone changes at most once, when the locations load.
  const calendarZone = useCalendarZone();
  const urlDefaults = useMemo(() => ledgerUrlDefaults(calendarZone), [calendarZone]);
  const urlState = useListUrlState(urlDefaults);
  const { values, set } = urlState;
  const tab = parseOption(values.tab, LEDGER_TABS, 'payments');
  const contactIdParam = values.contactId.trim();
  const contactId = contactIdParam.length > 0 ? contactIdParam : undefined;
  // The member's display name, known from a pick or resolved below. The URL carries only
  // the id, so a deep link or a reload starts with the name unknown.
  const [knownName, setKnownName] = useState<{ contactId: string; name: string } | null>(null);
  // DNKADM-0379 U1: a deep link carries the id but not the name. The name is `null`
  // until the member-detail read resolves it — the raw id is never shown as a person.
  const member: LedgerMember | null =
    contactId === undefined
      ? null
      : { contactId, name: knownName?.contactId === contactId ? knownName.name : null };
  const [pickerOpen, setPickerOpen] = useState(false);

  const memberNameUnresolved = member !== null && member.name === null;
  // A member change is a filter change: it resets the Payments page.
  const pickMember = (picked: { contactId: string; name: string }) => {
    setKnownName(picked);
    set({ contactId: picked.contactId, page: null });
  };
  const clearMember = () => set({ contactId: null, page: null });

  // DNKADM-0379 U1: resolve a deep-linked member's display name through the existing
  // member-detail read (`GET /v2/admin/members/{contactId}`) — no new endpoint. Best
  // effort and silent: a failure or an empty result leaves the chip reading "Member",
  // never clears the filter, never blocks the ledger read (which keys off `contactId`
  // alone), and raises no toast. The name is stored against its `contactId`, and the
  // functional update never replaces a name already known for that member, so a pick
  // made while the read is in flight is never overwritten.
  useEffect(() => {
    if (!memberNameUnresolved || contactId === undefined) return;
    let cancelled = false;
    void getMember(contactId)
      .then((detail) => {
        if (cancelled) return;
        const name = nonEmpty(detail.name) ?? nonEmpty(detail.email);
        if (name === null) return;
        setKnownName((current) =>
          current !== null && current.contactId === contactId ? current : { contactId, name },
        );
      })
      .catch(() => {
        /* silent by design — see above */
      });
    return () => {
      cancelled = true;
    };
  }, [contactId, memberNameUnresolved]);

  return (
    <Box>
      <Typography variant="h1" component="h1">
        Transaction ledger
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 680 }}>
        {LEDGER_TAB_COPY[tab]}
      </Typography>

      <Tabs
        value={tab}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        onChange={(_, next: LedgerTab) => set({ tab: next })}
        aria-label="Transaction ledger sections"
        sx={{ mt: 2, borderBottom: `1px solid ${t.lineSoft}` }}
      >
        <Tab
          label="Payments"
          value="payments"
          {...transactionLedgerTabA11yProps('payments')}
        />
        <Tab
          label="Upcoming (card-on-file)"
          value="upcoming-card-on-file"
          {...transactionLedgerTabA11yProps('upcoming-card-on-file')}
        />
        <Tab
          label="Upcoming memberships"
          value="upcoming-memberships"
          {...transactionLedgerTabA11yProps('upcoming-memberships')}
        />
      </Tabs>

      {tab === 'payments' ? (
        <Box {...transactionLedgerPanelA11yProps('payments')}>
          <Typography variant="h2" component="h2" sx={{ mt: 2 }}>
            Needs action
          </Typography>
          {/* Pending-charge queue reused heading-less — filters, dialogs, and API
              unchanged (DNKADM-0154 / DNKADM-0377). Quiet empty layout
              (DNKADM-0378) hides filters + table when the queue has no rows. */}
          <UpcomingChargesQueue emptyLayout="quiet" />

          {pickerOpen && (
            <MemberSearchDialog onClose={() => setPickerOpen(false)} onPick={pickMember} />
          )}

          {/* The member filter drives the Payments ledger only — the Needs action
              queue has no contactId filter (DNKADM-0154). It sits in the ledger's
              toolbar (DNKADM-0430 P1-B). */}
          <PaymentsLedger
            urlState={urlState}
            urlDefaults={urlDefaults}
            canReverse={!isStaffTier}
            {...(contactId !== undefined ? { contactId } : {})}
            memberFilter={
              <MemberFilter
                member={member}
                onOpen={() => setPickerOpen(true)}
                onClear={clearMember}
              />
            }
          />
        </Box>
      ) : tab === 'upcoming-card-on-file' ? (
        <Box {...transactionLedgerPanelA11yProps('upcoming-card-on-file')}>
          <UpcomingCardOnFileForecast />
        </Box>
      ) : (
        <Box {...transactionLedgerPanelA11yProps('upcoming-memberships')}>
          <UpcomingMembershipsForecast />
        </Box>
      )}
    </Box>
  );
}

/**
 * The Payments ledger's member filter. `name` is `null` while a deep-linked member's
 * name is unresolved (DNKADM-0379 U1) — the chip then reads "Member", never the id.
 */
type LedgerMember = { contactId: string; name: string | null };

/**
 * The shared member-filter row (DNKADM-0379 U2): the current selection AND the way back
 * to the picker at the same time. With a member set, the chip shows it — clicking the chip
 * reopens the picker to replace the selection, its delete affordance clears the filter —
 * and the "Filter by member" button stays beside it. With no member, the button alone, as
 * before.
 */
function MemberFilter({
  member,
  onOpen,
  onClear,
}: {
  member: LedgerMember | null;
  onOpen: () => void;
  onClear: () => void;
}) {
  return (
    <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      {member !== null && (
        <Chip
          icon={<Icon name="person" sx={{ fontSize: 18 }} />}
          label={member.name === null ? 'Member' : `Member: ${member.name}`}
          clickable
          onClick={onOpen}
          onDelete={onClear}
        />
      )}
      <Button
        size="small"
        variant="outlined"
        startIcon={<Icon name="person_search" sx={{ fontSize: 18 }} />}
        onClick={onOpen}
      >
        Filter by member
      </Button>
    </Stack>
  );
}

// --- Upcoming card-on-file forecast -------------------------------------------

function UpcomingCardOnFileForecast() {
  const t = resolveTokens(useTheme());
  const { selectedId } = useServiceLocation();
  const { currency } = useTenantCurrency();
  // DNKADM-0430 P1-B: the switch and the page live in the URL (`cofPage` is 1-indexed).
  const { values, set } = useListUrlState(CARD_ON_FILE_URL_DEFAULTS);
  const within24h = values.cof24h === SWITCH_ON;
  const page = parsePage(values.cofPage) - 1;

  const reservations = useResource(
    useCallback(
      () =>
        listCardOnFileReservations({
          page,
          size: DEFAULT_PAGE_SIZE,
          ...(within24h ? { withinHours: CARD_ON_FILE_WITHIN_HOURS } : {}),
          // Operator resolution for DNKADM-0215 U2: scope to the currently selected
          // app-bar service location when available; the selector has no all-locations state.
          ...(selectedId !== null ? { locationId: selectedId } : {}),
        }),
      [page, within24h, selectedId],
    ),
    [page, within24h, selectedId],
  );

  const items = reservations.data?.content ?? [];
  const totalPages = reservations.data?.totalPages ?? 1;
  const currencyCode = currency ?? FALLBACK_CURRENCY;
  const pageTotal = items.reduce((sum, reservation) => sum + effectiveNoShowCharge(reservation), 0);

  const toggleWithin24h = (next: boolean) =>
    set({ cof24h: next ? SWITCH_ON : null, cofPage: null });
  const goToPage = (next: number) => set({ cofPage: toPageParam(next + 1) });

  return (
    <Box>
      <ListToolbar
        filters={
          <FormControlLabel
            control={
              <Switch checked={within24h} onChange={(e) => toggleWithin24h(e.target.checked)} />
            }
            label="Within 24 hours"
          />
        }
        onClear={() => set({ cof24h: null, cofPage: null })}
        canClear={within24h}
      />

      <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary', maxWidth: 680 }}>
        No-show charges shown here are potential amounts, not guaranteed charges.
      </Typography>

      {reservations.status === 'error' ? (
        <ScreenError
          variant="panel"
          error={reservations.error}
          what="upcoming card-on-file bookings"
          onRetry={reservations.refetch}
          retryMessage="Something went wrong while loading the forecast."
          sx={{ mt: 2 }}
        />
      ) : (
        <Paper elevation={1} sx={{ mt: 2.25, overflow: 'hidden' }}>
          <Table aria-label="Upcoming card-on-file bookings" size="small">
            <TableHead>
              <TableRow>
                <TableCell>Start</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Masked card</TableCell>
                <TableCell align="right">No-show charge</TableCell>
                <TableCell align="right">Reservation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reservations.status === 'loading' &&
                Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                  <CardOnFileSkeletonRow key={`cof-sk-${i}`} />
                ))}

              {reservations.status === 'ready' && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <CardOnFileEmptyState />
                  </TableCell>
                </TableRow>
              )}

              {reservations.status === 'ready' &&
                items.map((reservation, index) => (
                  <CardOnFileReservationRow
                    key={reservation.reservationUuid ?? `cof-${index}`}
                    reservation={reservation}
                    currency={currencyCode}
                  />
                ))}
            </TableBody>
          </Table>

          {reservations.status === 'ready' && items.length > 0 && (
            <Stack
              direction="row"
              sx={{
                justifyContent: 'flex-end',
                alignItems: 'baseline',
                gap: 1,
                px: 2,
                py: 1.25,
                borderTop: `1px solid ${t.lineFaint}`,
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Page total (potential no-show charges)
              </Typography>
              <Typography sx={{ fontWeight: 700 }}>{formatMoney(pageTotal, currencyCode)}</Typography>
            </Stack>
          )}
          {/* Past the first page the pager stays even when the page is empty (a stale
              link), so Previous is the way back. */}
          {reservations.status === 'ready' && (items.length > 0 || page > 0) && (
            <CardOnFilePagination
              page={page}
              totalPages={totalPages}
              onPrev={() => goToPage(Math.max(0, page - 1))}
              onNext={() => goToPage(page + 1)}
            />
          )}
        </Paper>
      )}
    </Box>
  );
}

function CardOnFileReservationRow({
  reservation,
  currency,
}: {
  reservation: Reservation;
  currency: string;
}) {
  const service = [reservation.serviceName, reservation.variationName]
    .filter((s): s is string => s !== null)
    .join(' · ');
  const t = resolveTokens(useTheme());
  const calendarZone = useCalendarZone();
  const amount = effectiveNoShowCharge(reservation);
  const reservationUuid = reservation.reservationUuid;

  return (
    <TableRow hover>
      <TableCell>{formatVenueDateTime(reservation.start, calendarZone)}</TableCell>
      <TableCell>
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <MemberNameLink
            name={reservation.contactName}
            contactId={reservation.contactId}
            fallback="—"
          />
          {reservation.status === 'unconfirmed' && (
            <Chip
              size="small"
              label="Unconfirmed"
              sx={{ bgcolor: t.warnBadgeBg, color: t.warnBadgeInk, fontWeight: 600 }}
            />
          )}
        </Stack>
      </TableCell>
      <TableCell>{service === '' ? '—' : service}</TableCell>
      <TableCell>{maskedCard(reservation)}</TableCell>
      {/* A null fee is "no charge will happen" (DNKAPI-0447) — the table's
          empty-cell em-dash, never $0.00 (which reads as a zero-dollar charge). */}
      <TableCell align="right">
        {reservation.noShowFee === null ? '—' : formatMoney(amount, currency)}
      </TableCell>
      <TableCell align="right">
        {reservationUuid !== null ? (
          <RouterLink
            to={`/reservations?reservation=${encodeURIComponent(reservationUuid)}`}
            style={{ fontSize: 12 }}
          >
            View reservation
          </RouterLink>
        ) : (
          '—'
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * The amount a no-show mark would charge this reservation, for the row and the
 * page total. DNKAPI-0447 contract (DNKADM-0292): a null `noShowFee` means **no
 * fee will be charged** — it contributes 0 and must NOT fall back to
 * `priceCharged`; that fallback fabricates money that will never be collected.
 * On this server-filtered card-on-file list a null means the fee resolved
 * non-positive (e.g. an explicit `0` variation fee), never "no card on file".
 */
function effectiveNoShowCharge(reservation: Reservation): number {
  return reservation.noShowFee ?? 0;
}

function maskedCard(reservation: Reservation): string {
  const brand = reservation.cardBrand;
  const last4 = reservation.cardLast4;
  if (brand !== null && last4 !== null) return `${brand} •••• ${last4}`;
  if (last4 !== null) return `•••• ${last4}`;
  return brand ?? '—';
}

/**
 * The booking start as written on the wire (`Aug 1, 10:00 AM`): `Reservation.start` is ISO-8601
 * with the venue offset and is rendered AS-IS, its date and clock read off the string like the
 * other reservation surfaces (DNKADM-0386). Only a value with no readable wall clock goes through
 * the shared instant formatter on the venue's clock (DNKADM-0476 F-05).
 */
function formatVenueDateTime(ts: string | null, timeZone: string | null): string {
  const day = monthDayLabel(ts);
  const clock = isoClockAsIs(ts);
  if (day !== null && clock !== null) return `${day}, ${clock}`;
  return formatInstant(ts, timeZone, DATE_TIME);
}

function CardOnFileSkeletonRow() {
  return (
    <TableRow>
      {Array.from({ length: 6 }).map((_, i) => (
        <TableCell key={i}>
          <Skeleton height={18} />
        </TableCell>
      ))}
    </TableRow>
  );
}

function CardOnFileEmptyState() {
  const theme = useTheme();
  return (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      <Icon name="event_available" sx={{ fontSize: 44, color: inkAlpha(theme, 0.2) }} />
      <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
        No upcoming card-on-file bookings
      </Typography>
    </Box>
  );
}

function CardOnFilePagination({
  page,
  totalPages,
  onPrev,
  onNext,
}: {
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const t = resolveTokens(useTheme());
  const atLastPage = page >= Math.max(1, totalPages) - 1;
  return (
    <Stack
      component="nav"
      aria-label="Pagination"
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${t.lineFaint}`,
      }}
    >
      <Typography variant="body2" aria-current="page">
        Page {page + 1}
      </Typography>
      <IconAction label="Previous page" size="small" onClick={onPrev} disabled={page <= 0}>
        <Icon name="chevron_left" />
      </IconAction>
      <IconAction label="Next page" size="small" onClick={onNext} disabled={atLastPage}>
        <Icon name="chevron_right" />
      </IconAction>
    </Stack>
  );
}

// --- Upcoming memberships forecast (DNKADM-0377) --------------------------------

function UpcomingMembershipsForecast() {
  const { selectedId } = useServiceLocation();
  // DNKADM-0430 P1-B: the page lives in the URL (`memPage` is 1-indexed). No toolbar: the
  // forecast has no in-screen filter (the server's `from`/`to` are not offered today).
  const { values, set } = useListUrlState(MEMBERSHIPS_URL_DEFAULTS);
  const page = parsePage(values.memPage) - 1;
  const goToPage = (next: number) => set({ memPage: toPageParam(next + 1) });

  const charges = useResource(
    useCallback(
      () =>
        listUpcomingMembershipCharges({
          page,
          size: DEFAULT_PAGE_SIZE,
          ...(selectedId !== null ? { locationId: selectedId } : {}),
        }),
      [page, selectedId],
    ),
    [page, selectedId],
  );

  const items = charges.data?.content ?? [];
  const totalPages = charges.data?.totalPages ?? 1;
  // DNKADM-0383: the page total is the SERVER's summary, never a client-side sum of row amounts — a
  // negotiated-rate row carries no amount, and a sum that quietly skipped it would be the wrong
  // forecast. No summary ⇒ no total (never computed here to fill the gap).
  const summary = charges.data?.summary ?? null;

  return (
    <Box>
      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', maxWidth: 680 }}>
        Known next membership charges for this location. Dates are on or about; amounts are
        estimates. Members with no trustworthy date (including some Square billing days) do not
        appear — that is not an error.
      </Typography>

      {charges.status === 'error' ? (
        <ScreenError
          variant="panel"
          error={charges.error}
          what="upcoming membership charges"
          onRetry={charges.refetch}
          retryMessage="Something went wrong while loading the forecast."
          sx={{ mt: 2 }}
        />
      ) : (
        <Paper elevation={1} sx={{ mt: 2.25, overflow: 'hidden' }}>
          <Table aria-label="Upcoming membership charges" size="small">
            <TableHead>
              <TableRow>
                <TableCell>Next charge</TableCell>
                <TableCell>Member</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Masked card</TableCell>
                <TableCell align="right">Amount (est.)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {charges.status === 'loading' &&
                Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                  <MembershipsSkeletonRow key={`mem-sk-${i}`} />
                ))}

              {charges.status === 'ready' && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <MembershipsEmptyState />
                  </TableCell>
                </TableRow>
              )}

              {charges.status === 'ready' &&
                items.map((row, index) => (
                  <MembershipChargeRow
                    key={`${row.contactId}-${row.nextBillingOn}-${row.planId ?? 'none'}-${index}`}
                    row={row}
                  />
                ))}
            </TableBody>
          </Table>

          {charges.status === 'ready' && items.length > 0 && (
            <MembershipsPageTotal summary={summary} />
          )}
          {/* Past the first page the pager stays even when the page is empty (a stale
              link), so Previous is the way back. */}
          {charges.status === 'ready' && (items.length > 0 || page > 0) && (
            <CardOnFilePagination
              page={page}
              totalPages={totalPages}
              onPrev={() => goToPage(Math.max(0, page - 1))}
              onNext={() => goToPage(page + 1)}
            />
          )}
        </Paper>
      )}
    </Box>
  );
}

/**
 * DNKADM-0383 — the page total, from the server's summary only. Three shapes: a total with no
 * caveat (every row priced); a total that says, next to the figure and in plain words, how many
 * rows it excludes; or no total with a statement of why (nothing on the page could be priced, or
 * the server sent no summary). Never `$0.00` for "nothing priced", and never a client-side sum.
 */
function MembershipsPageTotal({ summary }: { summary: UpcomingMembershipChargesSummary | null }) {
  const t = resolveTokens(useTheme());
  const frame = {
    px: 2,
    py: 1.25,
    borderTop: `1px solid ${t.lineFaint}`,
  } as const;

  if (summary === null) {
    return (
      <Typography variant="body2" sx={{ ...frame, color: 'text.secondary', textAlign: 'right' }}>
        No page total — the server did not return one for this page.
      </Typography>
    );
  }
  if (summary.pricedAmount === null || summary.currency === null) {
    return (
      <Typography variant="body2" sx={{ ...frame, color: 'text.secondary', textAlign: 'right' }}>
        No page total — none of the rows on this page has a server estimate. Negotiated rates are
        set per member.
      </Typography>
    );
  }
  const rowsOnPage = summary.pricedRows + summary.unpricedRows;
  return (
    <Stack sx={{ ...frame, alignItems: 'flex-end', gap: 0.25 }}>
      <Stack direction="row" sx={{ alignItems: 'baseline', gap: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Page total (estimated)
        </Typography>
        <Typography sx={{ fontWeight: 700 }}>
          {formatMoney(summary.pricedAmount, summary.currency)}
        </Typography>
      </Stack>
      {summary.unpricedRows > 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Excludes {summary.unpricedRows} of {rowsOnPage} rows with no server estimate — negotiated
          rates are set per member.
        </Typography>
      )}
    </Stack>
  );
}

function MembershipChargeRow({ row }: { row: UpcomingMembershipCharge }) {
  const t = resolveTokens(useTheme());
  return (
    <TableRow hover>
      <TableCell>
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>on or about {formatApiDate(row.nextBillingOn)}</span>
          {row.pauseEffectiveOn !== null && (
            <Chip
              size="small"
              label={`Pause on ${formatApiDate(row.pauseEffectiveOn)}`}
              sx={{ bgcolor: t.warnBadgeBg, color: t.warnBadgeInk, fontWeight: 600 }}
            />
          )}
        </Stack>
      </TableCell>
      <TableCell>
        <MemberNameLink name={row.contactName} contactId={row.contactId} />
      </TableCell>
      <TableCell>{row.planName ?? '—'}</TableCell>
      <TableCell>{row.maskedCard ?? '—'}</TableCell>
      <TableCell align="right">
        {row.amount !== null && row.currency !== null ? (
          formatMoney(row.amount, row.currency)
        ) : row.rateBasis === 'negotiated' ? (
          /* DNKADM-0383: the marker, never a dash that reads as zero, a blank that reads as free, or
             the plan's list price. The figure lives with the member. */
          <Chip
            size="small"
            label="Negotiated rate"
            sx={{ bgcolor: t.successSoft, color: t.successSoftInk, fontWeight: 600 }}
          />
        ) : (
          '—'
        )}
      </TableCell>
    </TableRow>
  );
}

function MembershipsSkeletonRow() {
  return (
    <TableRow>
      {Array.from({ length: 5 }).map((_, i) => (
        <TableCell key={i}>
          <Skeleton height={18} />
        </TableCell>
      ))}
    </TableRow>
  );
}

function MembershipsEmptyState() {
  const theme = useTheme();
  return (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      <Icon name="event_available" sx={{ fontSize: 44, color: inkAlpha(theme, 0.2) }} />
      <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
        No memberships with a known next charge in this window.
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 420, mx: 'auto' }}>
        Members with no trustworthy date (including Square billing on the 29th–31st) do not
        appear — that is not an error.
      </Typography>
    </Box>
  );
}

// --- Payments ledger -----------------------------------------------------------

/**
 * A `from`/`to` URL value: a real `YYYY-MM-DD` calendar day, `''` (cleared), or else the
 * default. An impossible day (`2026-02-30`, `2026-13-01`) falls back too: dink-api binds
 * these as a strict ISO `LocalDate` and would answer `400`.
 */
function parseDateParam(raw: string, fallback: string): string {
  if (raw === '') return raw;
  if (!ISO_DATE.test(raw)) return fallback;
  const [y, m, d] = raw.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d
    ? raw
    : fallback;
}

function PaymentsLedger({
  contactId,
  urlState,
  urlDefaults,
  canReverse,
  memberFilter,
}: {
  contactId?: string;
  urlState: ListUrlState<LedgerUrlDefaults>;
  urlDefaults: LedgerUrlDefaults;
  /** Order Refund/Void — OWNER/ADMIN on the server, so false for STAFF (DNKADM-0375). */
  canReverse: boolean;
  memberFilter: ReactNode;
}) {
  // Location scope comes from the app-bar chooser, not an in-screen filter (DNKADM-0153).
  const { selectedId } = useServiceLocation();
  // Front Desk view hides the revenue rollup — a shoulder-surfing mitigation for
  // the counter iPad, not access control (DNKADM-0307). The ledger itself stays.
  const { frontDeskView } = useFrontDeskView();
  // Venue IANA zone of the selected location, else the tenant's, else the device's
  // (DNKADM-0478 F-223).
  const venueTimeZone = useCalendarZone();
  // DNKADM-0430 P1-B: the filters and the page are the URL's, validated here so a
  // hand-edited or stale link falls back instead of breaking. `''` is "no filter".
  const { values, set } = urlState;
  const from = parseDateParam(values.from, urlDefaults.from);
  const to = parseDateParam(values.to, urlDefaults.to);
  const kind = parseOption<string>(values.kind, PAYMENT_TRANSACTION_KINDS, '');
  const status = parseOption<string>(values.status, PAYMENT_TRANSACTION_STATUSES, '');
  const page = parsePage(values.page);

  const txns = useResource(
    useCallback(
      () =>
        listPaymentTransactions({
          page,
          size: DEFAULT_PAGE_SIZE,
          ...(contactId !== undefined ? { contactId } : {}),
          ...(kind === '' ? {} : { kind }),
          ...(status === '' ? {} : { status }),
          // Scope to the app service location; omit while none is resolved (tenant-wide).
          ...(selectedId !== null ? { locationRef: selectedId } : {}),
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
        }),
      [page, contactId, kind, status, selectedId, from, to],
    ),
    [page, contactId, kind, status, selectedId, from, to],
  );

  // Revenue rollup for the current date window (best-effort — a failure hides the
  // strip). In Front Desk view the fetch is skipped, not just the render — the
  // totals should be neither in memory nor in the network log (DNKADM-0307);
  // exiting the view re-runs the effect and fetches then.
  const revenue = useResource(
    useCallback(
      () =>
        frontDeskView
          ? Promise.resolve(null)
          : getRevenueByLocation({ ...(from ? { from } : {}), ...(to ? { to } : {}) }),
      [frontDeskView, from, to],
    ),
    [frontDeskView, from, to],
  );

  const items = txns.data?.items ?? [];
  const totalPages = txns.data?.totalPages ?? null;

  const txnsRefetch = txns.refetch;
  const revenueRefetch = revenue.refetch;
  // A reversal (DNKADM-0251) re-reads the ledger page — the reversed row's new status
  // plus the NEW refund row it creates. Verified: the revenue strip is its OWN resource
  // (`getRevenueByLocation`), not a derivation of the ledger page, so it does not update
  // from that re-read and needs its own refetch to reflect the reversed money.
  const handleReversed = useCallback(() => {
    txnsRefetch();
    revenueRefetch();
  }, [txnsRefetch, revenueRefetch]);

  // One `set` per action; every filter change resets the page. A date emptied in its
  // input is written as `from=`/`to=` (no bound), which survives a reload.
  const filtersActive =
    from !== urlDefaults.from ||
    to !== urlDefaults.to ||
    kind !== '' ||
    status !== '' ||
    contactId !== undefined;
  // Clear returns the ledger to its default view (last 30 days, every type and status, no
  // member); the tab and the other panels' params are left alone.
  const clearFilters = () =>
    set({ from: null, to: null, kind: null, status: null, contactId: null, page: null });

  return (
    <Box>
      <ListToolbar
        dateRange={{
          from,
          to,
          onFromChange: (v) => set({ from: v, page: null }),
          onToChange: (v) => set({ to: v, page: null }),
        }}
        filters={
          <>
            <ListSelectFilter
              label="Type"
              value={kind}
              onChange={(v) => set({ kind: v, page: null })}
              allLabel="All types"
              options={PAYMENT_TRANSACTION_KIND_OPTIONS}
            />
            <ListSelectFilter
              label="Status"
              value={status}
              onChange={(v) => set({ status: v, page: null })}
              allLabel="All statuses"
              options={PAYMENT_TRANSACTION_STATUS_OPTIONS}
            />
            {memberFilter}
          </>
        }
        onClear={clearFilters}
        canClear={filtersActive}
      />

      {!frontDeskView &&
        revenue.status === 'ready' &&
        revenue.data !== null &&
        revenue.data.groups.length > 0 && <RevenueStrip groups={revenue.data.groups} />}

      {txns.status === 'error' ? (
        <ScreenError
          variant="panel"
          error={txns.error}
          what="payments"
          onRetry={txns.refetch}
          retryMessage="Something went wrong while loading the ledger."
          sx={{ mt: 2.25 }}
        />
      ) : (
        <Paper elevation={1} sx={{ mt: 2.25, overflow: 'hidden' }}>
          {txns.status === 'loading' && <SkeletonBlock />}
          {txns.status === 'ready' && items.length === 0 && <EmptyState />}
          {txns.status === 'ready' && items.length > 0 && (
            <PaymentTransactionsTable
              rows={items}
              {...(canReverse ? { onReversed: handleReversed } : {})}
              timeZone={venueTimeZone}
            />
          )}
          {/* Past the first page the pager stays even when the page is empty (a stale
              link), so Previous is the way back. */}
          {txns.status === 'ready' && (items.length > 0 || page > 1) && (
            <Pagination
              page={page}
              totalPages={totalPages}
              itemCount={items.length}
              onPrev={() => set({ page: toPageParam(Math.max(1, page - 1)) })}
              onNext={() => set({ page: toPageParam(page + 1) })}
            />
          )}
        </Paper>
      )}
    </Box>
  );
}

const PAYMENT_TRANSACTION_KIND_OPTIONS = PAYMENT_TRANSACTION_KINDS.map((k) => ({
  value: k,
  label: humanizeToken(k),
}));
const PAYMENT_TRANSACTION_STATUS_OPTIONS = PAYMENT_TRANSACTION_STATUSES.map((s) => ({
  value: s,
  label: humanizeToken(s),
}));

/** The compact revenue rollup strip above the Payments ledger (best-effort groups). */
function RevenueStrip({
  groups,
}: {
  groups: { locationRef: string | null; currency: string | null; status: string | null; total: number }[];
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const locationName = useLocationName();
  const money = (total: number, currency: string | null) => {
    if (currency === null) return total.toFixed(2);
    try {
      return formatMoney(total, currency);
    } catch {
      return total.toFixed(2);
    }
  };
  return (
    <Paper elevation={0} sx={{ mt: 2.25, p: 1.5, bgcolor: t.raise }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: t.textSubtle, mb: 1 }}>
        Revenue for this window
      </Typography>
      <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
        {groups.map((g, i) => (
          <Box
            key={`${g.locationRef ?? 'all'}-${g.currency ?? ''}-${g.status ?? ''}-${i}`}
            sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: t.surface, border: `1px solid ${t.lineSoft}` }}
          >
            <Typography sx={{ fontSize: 11, color: inkAlpha(theme, 0.5) }}>
              {[locationName(g.locationRef) ?? (g.locationRef === null ? null : 'Unknown location'), g.status !== null ? humanizeToken(g.status) : null]
                .filter((s): s is string => s !== null && s.length > 0)
                .join(' · ') || 'All'}
            </Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{money(g.total, g.currency)}</Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

// --- Shared states -------------------------------------------------------------

function SkeletonBlock() {
  return (
    <Box sx={{ px: 3, py: 2 }}>
      {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
        <Skeleton key={`sk-${i}`} height={28} sx={{ my: 0.5 }} />
      ))}
    </Box>
  );
}

function EmptyState() {
  const theme = useTheme();
  return (
    <Box sx={{ py: 7, textAlign: 'center' }}>
      <Icon name="account_balance" sx={{ fontSize: 46, color: inkAlpha(theme, 0.2) }} />
      <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
        No transactions for these filters
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 380, mx: 'auto' }}>
        Try widening the date range or clearing a filter.
      </Typography>
    </Box>
  );
}

function Pagination({
  page,
  totalPages,
  itemCount,
  onPrev,
  onNext,
}: {
  page: number;
  totalPages: number | null;
  itemCount: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  // With a known page count, stop at the last page; otherwise offer Next until a
  // short (final) page arrives.
  const t = resolveTokens(useTheme());
  const atLastPage =
    totalPages !== null ? page >= Math.max(1, totalPages) : itemCount < DEFAULT_PAGE_SIZE;

  return (
    <Stack
      component="nav"
      aria-label="Pagination"
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${t.lineFaint}`,
      }}
    >
      <Typography variant="body2" aria-current="page">
        Page {page}
      </Typography>
      <IconAction label="Previous page" size="small" onClick={onPrev} disabled={page <= 1}>
        <Icon name="chevron_left" />
      </IconAction>
      <IconAction label="Next page" size="small" onClick={onNext} disabled={atLastPage}>
        <Icon name="chevron_right" />
      </IconAction>
    </Stack>
  );
}
